import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Botao } from './ui';
import css from './AvisoCookies.module.css';
import { Modal } from './Modal';
import {
  gravarConsentimento,
  lerConsentimento,
  type Consentimento,
} from '../util/consentimento';

/**
 * Aviso de cookies.
 *
 * Três decisões que o diferenciam do banner de praxe:
 *
 * 1. **"Recusar" tem o mesmo peso visual que "Aceitar".** Esconder a recusa
 *    atrás de um link cinza é o padrão escuro que a ANPD aponta como
 *    consentimento viciado — não é livre quem só encontra um caminho.
 * 2. **Nada é medido antes da escolha.** Enquanto o banner estiver aberto, o
 *    contador não roda. Um banner que só informa o que já foi feito é enfeite.
 * 3. **Duas categorias, porque só existem duas.** Não há cookie de publicidade
 *    nem de rede social neste site; listar categorias vazias para parecer
 *    completo seria mentir com aparência de zelo.
 *
 * Fechar no `Esc` ou no X não conta como aceite — o banner não é fechável sem
 * decidir, e a única forma de sair dele é escolhendo.
 */
export function AvisoCookies() {
  const [decisao, setDecisao] = useState<Consentimento | null | undefined>(
    undefined,
  );
  const [ajustando, setAjustando] = useState(false);
  const [audiencia, setAudiencia] = useState(true);

  useEffect(() => {
    setDecisao(lerConsentimento());
    // A tela de privacidade tem um botão para rever a escolha; sem ouvir o
    // evento, o banner só reapareceria depois de recarregar a página.
    const aoMudar = () => setDecisao(lerConsentimento());
    window.addEventListener('bx:consentimento', aoMudar);
    return () => window.removeEventListener('bx:consentimento', aoMudar);
  }, []);

  // `undefined` = ainda lendo o armazenamento. Não pisca o banner antes disso.
  if (decisao !== null) return null;

  function decidir(comAudiencia: boolean) {
    setDecisao(gravarConsentimento(comAudiencia));
    setAjustando(false);
  }

  return (
    <>
      <div role="dialog" aria-label="Aviso de cookies" className={css.aviso}>
        <div className={css.texto}>
          <span className={css.titulo}>
            <Cookie aria-hidden="true" />
            Cookies e privacidade
          </span>
          <p>
            Usamos cookies <b>necessários</b> para manter você conectado e
            proteger o acesso — sem eles o login não funciona. Além disso,
            gostaríamos de <b>medir quantas pessoas visitam o site</b>, de forma
            anônima: não guardamos seu IP nem usamos rastreadores de terceiros.
            Você escolhe.
          </p>
          <Link to="/privacidade" className={css.politica}>
            Ler a Política de Privacidade
          </Link>
        </div>

        <div className={css.botoes}>
          <Botao pequeno onClick={() => decidir(true)}>
            Aceitar tudo
          </Botao>
          {/* Mesmo tamanho e destaque do "Aceitar": recusar não pode ser
              mais difícil do que consentir. */}
          <Botao pequeno aparencia="primario" onClick={() => decidir(false)}>
            Recusar opcionais
          </Botao>
          <Botao
            pequeno
            aparencia="secundario"
            onClick={() => setAjustando(true)}
            className={css.personalizar}
          >
            Personalizar
          </Botao>
        </div>
      </div>

      <Modal
        aberto={ajustando}
        aoFechar={() => setAjustando(false)}
        titulo="Preferências de cookies"
        descricao="Escolha o que podemos usar. A decisão fica só no seu navegador e pode ser mudada quando quiser."
      >
        <div className={css.categorias}>
          <Categoria
            titulo="Necessários"
            obrigatorio
            marcado
            aoMudar={() => undefined}
            descricao="Mantêm sua sessão aberta (bx_sessao, bx_refresh) e impedem que outro site execute ações na sua conta (bx_csrf). Sem eles não há login. Não dependem de consentimento porque são parte do serviço que você contratou."
          />
          <Categoria
            titulo="Medição de audiência"
            marcado={audiencia}
            aoMudar={setAudiencia}
            descricao="Conta quantas pessoas abrem a página e por onde chegaram. O servidor guarda um código derivado do seu IP e navegador que muda todos os dias — nunca o IP em si — então não dá para identificar você nem seguir sua navegação entre sites. Nenhum dado vai para terceiros."
          />

          <div className={css.botoesModal}>
            <Botao onClick={() => decidir(audiencia)}>
              Salvar preferências
            </Botao>
            <Botao aparencia="contorno" onClick={() => decidir(false)}>
              Só os necessários
            </Botao>
          </div>
        </div>
      </Modal>
    </>
  );
}

function Categoria({
  titulo,
  descricao,
  marcado,
  obrigatorio,
  aoMudar,
}: {
  titulo: string;
  descricao: string;
  marcado: boolean;
  obrigatorio?: boolean;
  aoMudar: (v: boolean) => void;
}) {
  return (
    <label className={css.categoria} data-obrigatorio={obrigatorio || undefined}>
      <input
        type="checkbox"
        checked={marcado}
        disabled={obrigatorio}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <span className={css.categoriaTexto}>
        <span className={css.categoriaTitulo}>
          {titulo}
          {obrigatorio && <span className={css.sempre}>sempre ativos</span>}
        </span>
        <span className={css.categoriaDescricao}>{descricao}</span>
      </span>
    </label>
  );
}
